import { createElement } from '../../core/dom/helpers.js';

// ---------------------------------------------------------------------------
// File grid — selectable thumbnail tiles for the media library
// ---------------------------------------------------------------------------
//
// Presentation-only: receives FileMeta items + callbacks, knows nothing about
// apiClient / MediaClient / data fetching. Renders a CSS-grid of tiles with
// roving tabindex (Arrow/Home/End per policies.js), multi-select, aria-busy.
// Tile names use textContent (no innerHTML) so no escaping bug is possible.

/**
 * @typedef {Object} FileGridOptions
 * @property {(item: import('../../contracts/file-selection.js').FileMeta) => void} [onActivate]
 *   Called on click/Enter/Space of a tile (open/preview intent).
 * @property {(ids: (string|number)[]) => void} [onSelectionChange]
 *   Called whenever the selection set changes. Receives a fresh array (the
 *   caller can't mutate internal state through it).
 * @property {boolean} [selectable] - Enable selection (checkbox + toggle). Default false.
 * @property {boolean} [multiple] - Allow more than one selected tile. Default true when selectable.
 * @property {(item: import('../../contracts/file-selection.js').FileMeta) => boolean} [isSelectable]
 *   Per-item gate (e.g. pick-mode `accept`). Non-selectable tiles render but
 *   can't be toggled. Default: everything selectable.
 */

/**
 * @typedef {Object} FileGridHandle
 * @property {(items: import('../../contracts/file-selection.js').FileMeta[]) => void} render
 * @property {(busy: boolean) => void} setBusy
 * @property {() => (string|number)[]} getSelectedIds
 * @property {((ids: (string|number)[]) => void)} setSelectedIds - Silently set which current items are selected (no onSelectionChange).
 * @property {() => void} clearSelection
 * @property {() => void} destroy
 */

/**
 * Mount a file grid into `el`.
 *
 * @param {HTMLElement} el
 * @param {FileGridOptions} [options]
 * @returns {FileGridHandle}
 */
export function createFileGrid(el, options = {}) {
  const {
    onActivate,
    onSelectionChange,
    selectable = false,
    multiple = true,
    isSelectable = () => true,
  } = options;

  // A4 ARIA: selectable mode is a multi-select listbox of option tiles;
  // non-selectable mode is a bare container of real <button> tiles (their own
  // button semantics carry activation — no list/listitem wrapping).
  const grid = createElement('div', { class: 'file-grid' });
  if (selectable) {
    grid.setAttribute('role', 'listbox');
    grid.setAttribute('aria-multiselectable', String(multiple));
  }
  el.appendChild(grid);

  /** @type {import('../../contracts/file-selection.js').FileMeta[]} */
  let currentItems = [];
  /** @type {Set<string|number>} */
  const selected = new Set();
  /** Index of the tile that currently owns the roving tabindex. */
  let rovingIndex = 0;

  /** @returns {HTMLElement[]} */
  function tiles() {
    return /** @type {HTMLElement[]} */ ([...grid.querySelectorAll('.file-grid__tile')]);
  }

  function emitSelection() {
    onSelectionChange?.([...selected]);
  }

  /** @param {string} dataId - The tile's data-id (always a string). */
  function toggle(dataId) {
    const item = currentItems.find(i => String(i.id) === dataId);
    if (!item || !isSelectable(item)) return;
    if (selected.has(item.id)) {
      selected.delete(item.id);
    } else {
      if (!multiple) selected.clear();
      selected.add(item.id);
    }
    syncSelectionClasses();
    emitSelection();
  }

  function syncSelectionClasses() {
    for (const tile of tiles()) {
      const id = tile.dataset.id;
      const item = currentItems.find(i => String(i.id) === id);
      const on = item != null && selected.has(item.id);
      tile.classList.toggle('file-grid__tile--selected', on);
      // Selectable tiles are role="option" → use aria-selected (not aria-pressed).
      if (selectable) tile.setAttribute('aria-selected', String(on));
    }
  }

  /** @param {number} next */
  function focusTile(next) {
    const all = tiles();
    if (all.length === 0) return;
    const clamped = Math.max(0, Math.min(next, all.length - 1));
    rovingIndex = clamped;
    for (let i = 0; i < all.length; i++) {
      all[i].tabIndex = i === clamped ? 0 : -1;
    }
    all[clamped].focus();
  }

  /** @param {KeyboardEvent} e */
  function onKeydown(e) {
    const all = tiles();
    if (all.length === 0) return;
    const idx = all.indexOf(/** @type {HTMLElement} */ (e.target));
    if (idx === -1) return;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        focusTile(idx + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        focusTile(idx - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusTile(0);
        break;
      case 'End':
        e.preventDefault();
        focusTile(all.length - 1);
        break;
      case 'Enter':
      case ' ':
        // Only selectable (div role="option") tiles need manual activation.
        // Non-selectable tiles are real <button>s — the browser fires a native
        // click on Enter/Space, which onClick handles → no double-activation.
        if (selectable) {
          e.preventDefault();
          toggle(all[idx].dataset.id ?? '');
        }
        break;
      default:
        break;
    }
  }

  /** @param {MouseEvent} e */
  function onClick(e) {
    const tile = /** @type {HTMLElement | null} */ (
      (/** @type {Element} */ (e.target)).closest('.file-grid__tile')
    );
    if (!tile || !grid.contains(tile)) return;
    const idx = tiles().indexOf(tile);
    if (idx === -1) return;
    focusTile(idx);
    if (selectable) toggle(tile.dataset.id ?? '');
    else onActivate?.(currentItems[idx]);
  }

  grid.addEventListener('keydown', onKeydown);
  grid.addEventListener('click', onClick);

  /** @param {import('../../contracts/file-selection.js').FileMeta} item @param {number} index */
  function buildTile(item, index) {
    // Selectable → div role="option" (listbox child); non-selectable → real <button>.
    const tile = selectable
      ? createElement('div', {
        class: 'file-grid__tile',
        role: 'option',
        'data-id': String(item.id),
        title: item.name,
      })
      : createElement('button', {
        type: 'button',
        class: 'file-grid__tile',
        'data-id': String(item.id),
        title: item.name,
      });
    tile.tabIndex = index === rovingIndex ? 0 : -1;

    const hasThumb = typeof item.thumbnail === 'string' && item.thumbnail !== '';
    const isImage = hasThumb || item.kind === 'image';
    if (isImage) {
      const img = /** @type {HTMLImageElement} */ (createElement('img', { class: 'file-grid__thumb', alt: '' }));
      // SVG and other images render via <img src> only (never inline) — matches
      // the upload-validator SVG trust posture.
      img.src = hasThumb ? /** @type {string} */ (item.thumbnail) : item.url;
      tile.appendChild(img);
    } else {
      const icon = createElement('div', { class: 'file-grid__icon' });
      icon.textContent = (item.extension || item.kind || 'file').toUpperCase();
      tile.appendChild(icon);
    }

    const name = createElement('span', { class: 'file-grid__name' });
    name.textContent = item.name;
    tile.appendChild(name);

    if (selectable) {
      const canSelect = isSelectable(item);
      tile.setAttribute('aria-selected', String(selected.has(item.id)));
      if (selected.has(item.id)) tile.classList.add('file-grid__tile--selected');
      if (!canSelect) {
        tile.classList.add('file-grid__tile--disabled');
        tile.setAttribute('aria-disabled', 'true');
      }
    }

    return tile;
  }

  return {
    render(items) {
      currentItems = items.slice();
      // Drop selections for items no longer present (preserve by id otherwise).
      // A1b: if any id was dropped, emit so the host re-syncs (e.g. hides the
      // action row for a now-gone selected row).
      let droppedAny = false;
      for (const id of [...selected]) {
        if (!currentItems.some(i => i.id === id)) {
          selected.delete(id);
          droppedAny = true;
        }
      }
      if (rovingIndex >= currentItems.length) rovingIndex = 0;

      grid.textContent = '';
      currentItems.forEach((item, i) => grid.appendChild(buildTile(item, i)));

      if (droppedAny) emitSelection();
    },

    setBusy(busy) {
      grid.setAttribute('aria-busy', String(busy));
      grid.classList.toggle('file-grid--busy', busy);
    },

    getSelectedIds() {
      return [...selected];
    },

    /**
     * Imperatively set which of the CURRENT items are selected (silent — does NOT
     * emit onSelectionChange). Used by a host that owns the master selection across
     * pages to re-project it onto the visible grid after a render. Ids not present
     * in the current items are ignored (they live in the host's master set).
     * @param {(string|number)[]} ids
     */
    setSelectedIds(ids) {
      selected.clear();
      const wanted = new Set(ids.map(String));
      for (const item of currentItems) {
        if (wanted.has(String(item.id)) && isSelectable(item)) {
          selected.add(item.id);
          // Single-select: project at most one (defensive — the host master is already
          // ≤1 in single mode, but keep the grid's invariant structural here too).
          if (!multiple) break;
        }
      }
      syncSelectionClasses();
    },

    clearSelection() {
      if (selected.size === 0) return;
      selected.clear();
      syncSelectionClasses();
      emitSelection();
    },

    destroy() {
      grid.removeEventListener('keydown', onKeydown);
      grid.removeEventListener('click', onClick);
      grid.remove();
      currentItems = [];
      selected.clear();
    },
  };
}
